/**
 * Zustand Results Store
 *
 * Global store for optimization results that provides:
 * - Multi-run support for scenario comparison
 * - Direct access without prop drilling
 * - DevTools integration
 * - Persistence across component remounts
 *
 * @module stores/resultsStore
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * Run status enum
 * @readonly
 * @enum {string}
 */
export const RunStatus = {
    IDLE: 'idle',
    SUBMITTING: 'submitting',
    POLLING: 'polling',
    COMPLETE: 'complete',
    ERROR: 'error',
    INFEASIBLE: 'infeasible',
};

/**
 * @typedef {Object} OptimizationRun
 * @property {string} runId - Unique run identifier (UUID)
 * @property {RunStatus} status - Current run status
 * @property {string|null} statusPhase - Solver phase (e.g., 'Building model', 'Solving')
 * @property {Object|null} inputs - Scenario inputs used for this run
 * @property {Object|null} outputs - Optimization outputs (sized systems, dispatch)
 * @property {Object|null} results - Full results including financial metrics
 * @property {Object|null} infeasibilityMetrics - Grid limit infeasibility data
 * @property {string|null} error - Error message if run failed
 * @property {number} startTime - Timestamp when run started
 * @property {number|null} endTime - Timestamp when run completed
 */

/**
 * @typedef {Object} ResultsStore
 * @property {Object.<string, OptimizationRun>} runs - Map of run ID to run data
 * @property {string|null} activeRunId - Currently active run ID
 * @property {string|null} scenarioId - Current scenario ID (for context)
 * @property {string|null} projectId - Current project ID (for context)
 */

/**
 * Initial state for a new run
 */
const createEmptyRun = (runId, inputs = null) => ({
    runId,
    status: RunStatus.SUBMITTING,
    statusPhase: null,
    inputs,
    outputs: null,
    results: null,
    infeasibilityMetrics: null,
    error: null,
    startTime: Date.now(),
    endTime: null,
});

/**
 * Results store using Zustand
 *
 * Usage:
 * ```jsx
 * import { useResultsStore } from '../stores/resultsStore';
 *
 * function ResultsPanel() {
 *     const { activeRun, setActiveRun } = useResultsStore();
 *     // ...
 * }
 * ```
 */
export const useResultsStore = create(
    devtools(
        (set, get) => ({
            // State
            runs: {},
            activeRunId: null,
            scenarioId: null,
            projectId: null,

            // Selectors (computed from state)
            /**
             * Get the active run data
             * @returns {OptimizationRun|null}
             */
            get activeRun() {
                const { runs, activeRunId } = get();
                return activeRunId ? runs[activeRunId] : null;
            },

            /**
             * Check if any run is in progress
             * @returns {boolean}
             */
            get isAnyRunning() {
                const { runs } = get();
                return Object.values(runs).some(
                    r => r.status === RunStatus.SUBMITTING || r.status === RunStatus.POLLING
                );
            },

            // Actions

            /**
             * Set the current project/scenario context
             */
            setContext: (projectId, scenarioId) => set({
                projectId,
                scenarioId,
            }),

            /**
             * Set the active run ID
             */
            setActiveRun: (runId) => set({ activeRunId: runId }),

            /**
             * Start a new optimization run
             * @param {string} runId - UUID for the run
             * @param {Object} inputs - Scenario inputs
             */
            startRun: (runId, inputs = null) => set((state) => ({
                runs: {
                    ...state.runs,
                    [runId]: createEmptyRun(runId, inputs),
                },
                activeRunId: runId,
            })),

            /**
             * Update run status during polling
             */
            updateRunStatus: (runId, status, statusPhase = null) => set((state) => ({
                runs: {
                    ...state.runs,
                    [runId]: {
                        ...state.runs[runId],
                        status,
                        statusPhase,
                    },
                },
            })),

            /**
             * Mark run as complete with results
             */
            completeRun: (runId, results) => set((state) => ({
                runs: {
                    ...state.runs,
                    [runId]: {
                        ...state.runs[runId],
                        status: RunStatus.COMPLETE,
                        outputs: results.outputs || {},
                        results,
                        endTime: Date.now(),
                    },
                },
            })),

            /**
             * Mark run as failed
             */
            failRun: (runId, error, infeasibilityMetrics = null) => set((state) => ({
                runs: {
                    ...state.runs,
                    [runId]: {
                        ...state.runs[runId],
                        status: infeasibilityMetrics ? RunStatus.INFEASIBLE : RunStatus.ERROR,
                        error,
                        infeasibilityMetrics,
                        endTime: Date.now(),
                    },
                },
            })),

            /**
             * Load saved results from backend storage
             * Used when loading a scenario with previous optimization
             */
            loadSavedRun: (runId, savedResults, inputs = null) => set((state) => ({
                runs: {
                    ...state.runs,
                    [runId]: {
                        runId,
                        status: RunStatus.COMPLETE,
                        statusPhase: null,
                        inputs,
                        outputs: savedResults.outputs || {},
                        results: savedResults,
                        infeasibilityMetrics: null,
                        error: null,
                        startTime: savedResults.timestamp || Date.now(),
                        endTime: savedResults.timestamp || Date.now(),
                    },
                },
                activeRunId: runId,
            })),

            /**
             * Clear a specific run
             */
            clearRun: (runId) => set((state) => {
                const { [runId]: removed, ...remaining } = state.runs;
                return {
                    runs: remaining,
                    activeRunId: state.activeRunId === runId ? null : state.activeRunId,
                };
            }),

            /**
             * Clear all runs for current scenario
             */
            clearAllRuns: () => set({
                runs: {},
                activeRunId: null,
            }),

            /**
             * Reset store to initial state
             */
            reset: () => set({
                runs: {},
                activeRunId: null,
                scenarioId: null,
                projectId: null,
            }),
        }),
        { name: 'results-store' }
    )
);

/**
 * Selector hooks for common patterns
 */

/**
 * Get active run data
 * @returns {OptimizationRun|null}
 */
export const useActiveRun = () => {
    const runs = useResultsStore((state) => state.runs);
    const activeRunId = useResultsStore((state) => state.activeRunId);
    return activeRunId ? runs[activeRunId] : null;
};

/**
 * Check if optimization is running
 * @returns {boolean}
 */
export const useIsRunning = () => {
    const runs = useResultsStore((state) => state.runs);
    return Object.values(runs).some(
        r => r.status === RunStatus.SUBMITTING || r.status === RunStatus.POLLING
    );
};

/**
 * Get all completed runs (for comparison)
 * @returns {OptimizationRun[]}
 */
export const useCompletedRuns = () => {
    const runs = useResultsStore((state) => state.runs);
    return Object.values(runs).filter(r => r.status === RunStatus.COMPLETE);
};

export default useResultsStore;
